import { Typography, Skeleton } from '@mui/material'
import { Delete, Visibility } from '@mui/icons-material'
import Button from 'components/molecules/Button/Button'
import { useAddTrayContext } from '../AddTray.context'
import { LoadingButton } from 'components/mui'
import { HeadCell } from 'components/molecules/DataTable/TableHeader'
import { MappedSubTrays, SubTray } from './SubTrays.types'
import { formatToTitleCase } from 'lib/utils/formatToTitleCase'
import { useTrayStepContext } from '../AddTray.logic'
import { useSubTraysContext } from './SubTrays.context'
import { useState } from 'react'
import useAnalyzeTrayLogic from '../AnalyzeTray/AnalyzeTray.logic'
import useDTMS3Image from 'lib/utils/useDTMS3Image'

// Create a reusable component for the tray image cell
const TrayImageCell = ({
  imageUrl,
  description,
}: {
  imageUrl: string
  description: string
}) => {
  const { imageUrl: trayImageSrc, isLoading } = useDTMS3Image({
    url: imageUrl,
    defaultImage: '',
  })

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width={180}
        height={90}
        animation="wave"
        className="tray_img tray_img_skeleton"
      />
    )
  }

  return (
    <img
      className="tray_img"
      alt={description || 'Sub-Tray Image'}
      src={trayImageSrc}
    />
  )
}

const useSubTraysLogic = () => {
  const {
    subTrays,
    setSubTrays,
    setSelectedTrayType,
    currentTrayIndex,
    updateCurrentTrayItem,
    saveCurrentTrayAsItem,
    setHasSubTrays,
  } = useAddTrayContext()
  const { resetStateForNextSubTray } = useAnalyzeTrayLogic()
  const { nextStep, prevStep } = useTrayStepContext()
  const {
    currentView,
    setCurrentView,
    selectedTrayIndex,
    setSelectedTrayIndex,
  } = useSubTraysContext()

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [trayToDelete, setTrayToDelete] = useState<number | null>(null)

  /**
   * Defines the header cells for the tray management table.
   */
  const subTraysTableHeaderCells: HeadCell[] = [
    { id: 'trayNumber', label: 'ID', numeric: true },
    { id: 'trayImg', label: 'Tray Image', numeric: false },
    { id: 'trayType', label: 'Tray Type', numeric: false },
    { id: 'parLevel', label: 'Par Level', numeric: false },
    { id: 'viewDetails', label: 'Tray Details', numeric: false },
    { id: 'deleteSubTray', label: 'Delete Sub-Tray', numeric: false },
  ]

  /**
   * Maps locally created sub trays into structured cells for the sub trays table body.
   */
  const subTraysTableBodyCells = (subTrays: SubTray[]): MappedSubTrays[] => {
    return subTrays.map((subTray, _index) => {
      return {
        trayNumber: `${_index + 1}`,
        trayImg: (
          <TrayImageCell
            imageUrl={subTray.trayImg}
            description={subTray.description || 'Sub-Tray Image'}
          />
        ),
        trayType: (
          <Typography variant="body1">
            {formatToTitleCase(subTray.trayType || 'Unknown Tray Type')}
          </Typography>
        ),
        parLevel: `${subTray.parLevel}`,
        viewDetails: (
          <Button
            startIcon={<Visibility />}
            onClick={() => handleViewDetailsClick(_index)}
          >
            View Details
          </Button>
        ),
        deleteSubTray: (
          <LoadingButton
            className="delete-sub-tray-button"
            mode="delete"
            sx={{ width: '30px', minWidth: '0px', maxWidth: '30px' }}
            onClick={() => handleDeleteSubTray(_index)}
          >
            <Delete sx={{ width: '17px', height: '17px' }} />
          </LoadingButton>
        ),
      }
    })
  }

  /**
   * Function to handle the "Add Instrument Tray" button click.
   */
  const handleAddInstrumentTrayClick = () => {
    resetStateForNextSubTray()
    setSelectedTrayType('instrument')
    setCurrentView('analyze')
  }

  /**
   * Function to handle the "Add Implant Tray" button click.
   */
  const handleAddImplantTrayClick = () => {
    resetStateForNextSubTray()
    setCurrentView('analyze')
  }

  /**
   * Function to handle the "Add Implant Tray" button click.
   */
  const handleAddCustomImplantTrayClick = () => {
    resetStateForNextSubTray()
    setCurrentView('analyze')
    setSelectedTrayType('custom implants')
  }

  /**
   * Function to handle the "Add Instrument w/ Sub Trays" button click.
   */
  const handleAddInstrumentWithSubTraysClick = () => {
    resetStateForNextSubTray()
    setCurrentView('analyze')
  }

  const handleAddInstrumentTrayWithImplantSubTraysClick = () => {
    setHasSubTrays(true)
  }

  /**
   * Handles the event when the user clicks to view details of a specific sub-tray.
   */
  const handleViewDetailsClick = (subTrayIndex: number) => {
    setSelectedTrayIndex(subTrayIndex)
    setCurrentView('details')
  }

  const handleDeleteSubTray = (subTrayIndex: number) => {
    setTrayToDelete(subTrayIndex)
    setShowDeleteConfirmation(true)
  }

  const handleConfirmDelete = () => {
    if (trayToDelete !== null) {
      const updatedSubTrays = subTrays.filter(
        (_subTray, _index) => _index !== trayToDelete
      )
      setSubTrays(updatedSubTrays)
      setShowDeleteConfirmation(false)
      setTrayToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false)
    setTrayToDelete(null)
  }

  const handleNext = () => {
    if (currentTrayIndex >= 0) {
      updateCurrentTrayItem()
    } else {
      saveCurrentTrayAsItem()
    }

    nextStep()
  }

  const handleBack = () => {
    prevStep()
  }

  return {
    handleAddInstrumentTrayClick,
    handleAddImplantTrayClick,
    handleAddInstrumentWithSubTraysClick,
    subTraysTableHeaderCells,
    subTraysTableBodyCells,
    handleNext,
    handleBack,
    currentView,
    selectedTrayIndex,
    showDeleteConfirmation,
    handleConfirmDelete,
    handleCancelDelete,
    handleAddCustomImplantTrayClick,
    handleAddInstrumentTrayWithImplantSubTraysClick,
  }
}

export default useSubTraysLogic
